import { EnumHelper, List, NeoModel, NotifyUtils } from "@singularsystems/neo-core";
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from "../../App/Services/AppService";
import StaticServiceProviderFileGroupingModel from "../../Common/Models/StaticServiceProviderFileGroupingModel";
import StaticServiceProviderImportDatesModel from "../Models/Lookups/StaticServiceProviderImportDatesModel";
import TotalHoldingsLookup from "../Models/Lookups/TotalHoldingsLookup";
import ProcessStatusSearchCriteria from "../Models/Queries/ProcessStatusSearchCriteria";
import ScheduleWACCommand from "../Models/Queries/ScheduleWACForRawImportLogIDCommand";
import StaticServiceProviderImportStatusModel from "../Models/Lookups/StaticServiceProviderImportStatusModel";
import DeleteProcessingFileCommand from "../Models/Queries/DeleteProcessingFileCommand";
import { TextConstants } from "../../Common/TextConstants";
import { NotificationDuration } from "../../App/Models/Enums/NotificationDuration";
import { ActionableItemsDropDownEnum } from "../../Common/Enums/ActionableItemsDropDownEnum";

@NeoModel
export default class HomeVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        public homeApiClient = AppService.get(Types.ApiClients.HomeApiClient),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        public generalApiClient = AppService.get(Types.ApiClients.GeneralApiClient)) {
        super(taskRunner);
    }

    public importDatesLookup = new List(StaticServiceProviderImportDatesModel);

    public isActive: boolean = true;
    public isActiveInner: boolean = true;
    public showLatestImportsButtonInd: boolean = false;
    public showDeleteConfirmation: boolean = false;
    public showCancelConfirmation: boolean = false;
    public showResetConfirmation: boolean = false;
    public rawImportFileId: number = 0;
    public fileName: string = "";

    public processStatusSearchCriteria = new ProcessStatusSearchCriteria();
    public fileGroupingList = new List(StaticServiceProviderFileGroupingModel);

    public holdingsList = new List(TotalHoldingsLookup);
    public hasHoldingChartLoaded: boolean = false;

    public actionableItems = EnumHelper.asList(ActionableItemsDropDownEnum).map(e => ({ text: e.name, data: e.id }));
    public selectedAction: string = "Please select an action";

    public processingFileIdToDelete: number = 0;

    public async initialise() {
        this.loadImportProcessStatusLastSevenDays();
        await this.loadFileGroupings();
        this.initChart();
    }

    public clearSearch() {
        this.processStatusSearchCriteria.staticServiceProviderFileGroupingID = null;
        this.processStatusSearchCriteria.date = null;
    }

    public toggleIsActive() {
        this.isActive = !this.isActive;
    }

    public toggleIsActiveInner() {
        this.isActiveInner = !this.isActiveInner;
    }

    public async loadFileGroupings() {
        const response = await this.taskRunner.waitFor(this.generalApiClient.getStaticServiceProviderFileGroupingList());
        this.fileGroupingList.set(response.data);
    }

    public async loadImportProcessStatusLastSevenDays() {
        const response = await this.taskRunner.waitFor(this.homeApiClient.getImportProcessStatus());
        this.showLatestImportsButtonInd = false;
        this.importDatesLookup.set(response.data);
    }

    public async loadImportProcessStatusWithSearchCriteria(fileGroupingID?: number, importDate?: Date) {
        const response = await this.taskRunner.waitFor(this.homeApiClient.getImportProcessStatus(fileGroupingID, importDate));
        this.showLatestImportsButtonInd = true;
        this.importDatesLookup.set(response.data);
    }

    private async runRawImportActionAndReload(action: () => Promise<unknown>) {
        await this.taskRunner.run(action);
        await this.loadImportProcessStatusLastSevenDays();
    }

    public async normalizeRawImportFileID(rawImportFileID: number) {
        await this.runRawImportActionAndReload(() => this.homeApiClient.normalizeRawImportFileID(rawImportFileID));
    }

    public async extractRawImportFileId(rawImportFileID: number) {
        await this.runRawImportActionAndReload(() => this.homeApiClient.extractRawImportFileId(rawImportFileID));
    }

    public async interceptRawImportFileID(rawImportFileID: number) {
        await this.runRawImportActionAndReload(() => this.homeApiClient.interceptRawImportFileID(rawImportFileID));
    }

    public async showDeleteModal(processingFileId: number) {
        this.showDeleteConfirmation = true;
        this.processingFileIdToDelete = processingFileId;
    }

    public async processAction(action: string, rawImportFileId: number) {

        switch (action) {
            case ActionableItemsDropDownEnum.Extraction.toString():
                await this.extractRawImportFileId(rawImportFileId);
                break;
            case ActionableItemsDropDownEnum.Interception.toString():
                await this.interceptRawImportFileID(rawImportFileId);
                break;
            case ActionableItemsDropDownEnum.Normalize.toString():
                await this.normalizeRawImportFileID(rawImportFileId);
                break;
            default:
                break;
        }
    }

    public async deleteProcessingFile(processingFileId: number, fileGroupingID?: number, importDate?: Date) {
        this.showDeleteConfirmation = false;
        var command = new DeleteProcessingFileCommand();
        command.processingFileId = processingFileId;
        var response = await this.taskRunner.run(() => this.homeApiClient.deleteProcessingFile(command));
        if (response.data.success) {
            NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.ProcessingFileDeleted, NotificationDuration.Standard);
        } else {
            NotifyUtils.addDanger(TextConstants.Titles.Error, response.data.error, NotificationDuration.Standard)
        }

        this.loadImportProcessStatusWithSearchCriteria(fileGroupingID, importDate);
    }

    public async scheduleWACForRawImportLogID(item: StaticServiceProviderImportStatusModel, date: Date) {
        if (item.rawImportLogID !== null) {
            var command = new ScheduleWACCommand();
            command.rawImportLogID = item.rawImportLogID;
            command.staticServiceProviderFileGroupingID = this.fileGroupingList.find(x => x.serviceProviderGroupingName === item.serviceProviderGroupingName)?.staticServiceProviderFileGroupingId!;
            command.startDateRange = date
            command.endDateRange = date;
            await this.taskRunner.run(() => this.homeApiClient.scheduleWACForRawImportLogID(command.toJSObject()));
        }
        else {
            NotifyUtils.addDanger(TextConstants.Titles.Error, TextConstants.Notifications.UnableToSetRawImportLog, NotificationDuration.Standard)
        }
    }

    public async initChart() {
        this.holdingsList.set((await this.serviceProviderApiClient.getTotalHoldings()).data);
        this.hasHoldingChartLoaded = true;
    }

    public showResetModal(rawImportFileId: number, fileName: string) {
        this.rawImportFileId = rawImportFileId;
        this.fileName = fileName;
        this.showResetConfirmation = true;
    }

    public showCancelModal(rawImportFileId: number, fileName: string) {
        this.rawImportFileId = rawImportFileId;
        this.fileName = fileName;
        this.showCancelConfirmation = true;
    }

    public async confirmResetRawImportFile() {
        if (this.rawImportFileId > 0) {
            await this.taskRunner.run(() => this.homeApiClient.resetRawImportFile(this.rawImportFileId!));
            // Efficiently update the statuses in the frontend
            for (const dateModel of this.importDatesLookup) {
                for (const statusModel of dateModel.staticServiceProviderImportStatusModel) {
                    const fileStatus = statusModel.fileImportProcessStatusModel.find(
                        (fs: any) => fs.rawImportFileID === this.rawImportFileId
                    );
                    if (fileStatus) {
                        if (fileStatus.rawFileImport === "Processing") fileStatus.rawFileImport = "Not started";
                        if (fileStatus.normalisation === "Processing") fileStatus.normalisation = "Not started";
                        if (fileStatus.extraction === "Processing") fileStatus.extraction = "Not started";
                        if (fileStatus.interception === "Processing") fileStatus.interception = "Not started";
                        fileStatus.fileImportStatus = "Pending";
                        break;
                    }
                }
            }
            this.showResetConfirmation = false;
        }
    }

    public async confirmCancelRawImportFile() {
        if (this.rawImportFileId > 0) {
            await this.taskRunner.run(() => this.homeApiClient.cancelRawImportFile(this.rawImportFileId!));
            // Efficiently update the statuses in the frontend
            for (const dateModel of this.importDatesLookup) {
                for (const statusModel of dateModel.staticServiceProviderImportStatusModel) {
                    const fileStatus = statusModel.fileImportProcessStatusModel.find(
                        (fs: any) => fs.rawImportFileID === this.rawImportFileId
                    );
                    if (fileStatus) {
                        if (fileStatus.rawFileImport === "Processing" || fileStatus.rawFileImport === "Not started") fileStatus.rawFileImport = "Cancelled";
                        else if (fileStatus.normalisation === "Processing" || fileStatus.normalisation === "Not started") fileStatus.normalisation = "Cancelled";
                        else if (fileStatus.extraction === "Processing" || fileStatus.extraction === "Not started") fileStatus.extraction = "Cancelled";
                        else if (fileStatus.interception === "Processing" || fileStatus.interception === "Not started") fileStatus.interception = "Cancelled";
                        // set the rawimportfile status to 'Fail for canceled files
                        fileStatus.fileImportStatus = "Fail";
                        break;
                    }
                }
            }
            this.showCancelConfirmation = false;
        }
    }
}